"use client";

import { useEffect, useSyncExternalStore } from "react";
import { isScrollLocked, lockScroll, subscribeScrollLock } from "@/utils/scrollLock";

/**
 * Hold the shared body scroll lock for as long as `enabled` is true.
 *
 * `lockScroll()` returns its own release function, which is exactly the shape
 * an effect cleanup wants — so an overlay unmounting mid-navigation releases
 * just as reliably as one closing normally.
 */
export default function useScrollLock(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    return lockScroll();
  }, [enabled]);
}

/**
 * Whether any overlay currently holds the lock — i.e. "is a modal already up".
 *
 * Interruptive UI (the lead popup) reads this so it never opens on top of, or
 * worse behind, something the user is in the middle of. The lock is a module
 * singleton mutated outside React, which is exactly what useSyncExternalStore
 * exists for: no effect-driven setState, no tearing, and the server snapshot is
 * pinned to false so hydration always matches.
 */
export function useIsScrollLocked() {
  return useSyncExternalStore(subscribeScrollLock, isScrollLocked, () => false);
}
